import { type DurationUnit } from '@aysar/contracts';
import { forwardRef, useId, useImperativeHandle, useRef, type KeyboardEvent } from 'react';

import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import { FIELD_SLOT_CLASSES } from '@/shared/components/ui/field/field.tokens';
import { Label } from '@/shared/components/ui/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { cn } from '@/shared/utils/cn/cn.util';

import { ScalarFieldIsland } from '../ScalarFieldIsland/ScalarFieldIsland';
import { ScalarHint } from '../ScalarHint/ScalarHint';
import { ScalarShell } from '../ScalarShell/ScalarShell';
import {
  DURATION_AMOUNT_CLASSES,
  DURATION_UNIT_CLASSES,
  DURATION_UNIT_STATIC_CLASSES,
  SCALAR_ADOPT_CLASSES,
  SCALAR_TOGGLE_LABEL_CLASSES,
  SCALAR_TOGGLE_ROW_CLASSES,
} from '../scalar.tokens';
import { type ScalarHandle } from '../types/scalar-field.types';

import { type DurationInputProps } from './DurationInput.types';
import { useDurationInputLogic } from './useDurationInputLogic';

/** What one press of each key is worth, as a multiple of the step. */
const KEY_STEPS: Readonly<Record<string, number>> = {
  ArrowUp: 1,
  ArrowDown: -1,
  PageUp: 10,
  PageDown: -10,
};

/**
 * A stored integer duration, edited in the unit that reads best.
 *
 * **Self-naming.** It renders two or three named controls — an amount, a unit,
 * and sometimes a "never expires" — so the group carries the field's name and
 * each child a composed one ("… (amount)", "… (unit)"). A single `<Label
 * htmlFor>` could only point at one of them, and pointing it at the amount would
 * leave the other two anonymous.
 *
 * The description is threaded onto each CONTROL rather than onto the group: a
 * group's description is not reliably announced on entry, so an operator who
 * tabs straight into the amount would never hear it. `ColorInput` carries the
 * same note for the same reason.
 */
export const DurationInput = forwardRef<ScalarHandle, DurationInputProps>(function DurationInput(
  {
    id,
    className,
    size,
    label,
    value,
    onValueChange,
    baseUnit,
    min,
    max,
    units,
    isNullable = false,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const foreverId = `${fieldId}-forever`;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const view = useDurationInputLogic({
    label,
    value,
    onValueChange,
    baseUnit,
    min,
    max,
    units,
    isNullable,
    isDisabled,
    isReadOnly,
  });

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    get element() {
      return inputRef.current;
    },
  }));

  const describe = cn(describedBy, hintId);
  const hasChoice = view.units.length > 1;

  return (
    <ScalarShell
      role="group"
      className={className}
      label={label}
      aria-describedby={describe}
      isReadOnly={isReadOnly}
      announcement={view.announcement}
      hint={
        <ScalarHint
          id={hintId}
          range={view.isForever ? undefined : view.rangeHint}
          warning={view.warning}
          action={
            view.adoptLabel === undefined ? undefined : (
              <button type="button" className={SCALAR_ADOPT_CLASSES} onClick={view.onAdopt}>
                {view.adoptLabel}
              </button>
            )
          }
        >
          {view.storedHint}
        </ScalarHint>
      }
    >
      {/*
        The amount field is REMOVED while "never expires" is on, not disabled: a
        greyed-out box beside a ticked box reads as "there is a number here you
        may not touch", and there is not — the value is `null`.
      */}
      {view.isForever ? null : (
        <ScalarFieldIsland
          size={size}
          isInvalid={isInvalid || view.number.isDraftInvalid}
          adornment={
            hasChoice ? (
              <Select
                value={view.unit}
                onValueChange={(next) => {
                  view.onUnitChange(next as DurationUnit);
                }}
                disabled={isDisabled}
              >
                <SelectTrigger
                  variant="slot"
                  className={DURATION_UNIT_CLASSES}
                  aria-label={view.unitLabel}
                  aria-describedby={describe}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {view.units.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {view.unitName(unit)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              // One offered unit is not a choice, so it is a word rather than a
              // dropdown that can only ever answer one way.
              <span className={DURATION_UNIT_STATIC_CLASSES}>{view.unitName(view.unit)}</span>
            )
          }
        >
          <input
            ref={inputRef}
            id={fieldId}
            // `text` + `inputMode`, not `type="number"`: a number input reports
            // an EMPTY value for an in-progress draft, which makes refusing a
            // fraction — the behaviour this control is built around —
            // impossible to see. Spinbutton semantics are supplied explicitly.
            type="text"
            inputMode="numeric"
            role="spinbutton"
            aria-label={view.amountLabel}
            aria-describedby={describe}
            aria-valuenow={view.number.draftValue}
            aria-valuemin={min === undefined ? undefined : min}
            aria-valuemax={max === undefined ? undefined : max}
            aria-valuetext={view.storedHint}
            aria-invalid={isInvalid || view.number.isDraftInvalid ? true : undefined}
            aria-readonly={isReadOnly ? true : undefined}
            value={view.number.text}
            disabled={isDisabled}
            readOnly={isReadOnly}
            autoComplete="off"
            onChange={(event) => {
              view.number.setText(event.target.value);
            }}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
              const steps = KEY_STEPS[event.key];
              if (steps === undefined) return;
              event.preventDefault();
              view.number.stepBy(event.shiftKey ? steps * 10 : steps);
            }}
            onBlur={() => {
              onBlur?.();
            }}
            className={cn(FIELD_SLOT_CLASSES, DURATION_AMOUNT_CLASSES)}
          />
        </ScalarFieldIsland>
      )}

      {view.canBeForever ? (
        <span className={SCALAR_TOGGLE_ROW_CLASSES}>
          <Checkbox
            id={foreverId}
            checked={view.isForever}
            disabled={isDisabled}
            aria-label={view.foreverLabel}
            aria-describedby={describe}
            onCheckedChange={(checked) => {
              view.onForeverChange(checked === true);
            }}
          />
          <Label htmlFor={foreverId} className={SCALAR_TOGGLE_LABEL_CLASSES}>
            {view.foreverLabel}
          </Label>
        </span>
      ) : null}
    </ScalarShell>
  );
});
